// ─── Feuille de style globale — direction « Clean Utility » ───────────────────
// Bordures nettes, ombres discrètes, densité élevée, animations sobres.

export const GLOBAL_CSS = `
  :root {
    --bg:            #f7f7f5;
    --surface:       #ffffff;
    --surface-alt:   #fafaf9;
    --surface-hover: #f4f4f2;
    --border:        #e4e4e1;
    --border-strong: #d0d0cb;
    --ink:           #0a2540;
    --ink-body:      #3f4a58;
    --ink-muted:     #64748b;
    --ink-faint:     #94a3b8;
    --navy:          #0a2540;
    --navy-hover:    #14395c;
    --navy-soft:     #eef2f6;
    --amber:         #d97706;
    --success:       #15803d;
    --danger:        #b91c1c;
    --radius:        6px;
    --font-sans:     -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    --font-mono:     ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace;
  }

  * { box-sizing: border-box; margin: 0; padding: 0; }

  html { -webkit-text-size-adjust: 100%; }

  body {
    font-family: var(--font-sans);
    background: var(--bg);
    color: var(--ink);
    font-size: 14px;
    line-height: 1.5;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  input, select, textarea, button { font-family: inherit; font-size: inherit; color: inherit; }
  button { cursor: pointer; background: none; border: none; }
  a { text-decoration: none; color: inherit; }

  /* Chiffres alignés partout où l'on affiche des données */
  table, .numeric { font-variant-numeric: tabular-nums; }

  /* ─── Accessibilité : anneau de focus visible au clavier uniquement ──── */
  :focus { outline: none; }
  :focus-visible {
    outline: 2px solid var(--navy);
    outline-offset: 2px;
    border-radius: 3px;
  }

  /* ─── Barres de défilement discrètes ─────────────────────────────────── */
  ::-webkit-scrollbar { width: 10px; height: 10px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: #d4d4d0; border-radius: 5px; border: 3px solid var(--bg); }
  ::-webkit-scrollbar-thumb:hover { background: #b4b4ae; }

  /* ─── Sélection de texte ─────────────────────────────────────────────── */
  ::selection { background: #0a254020; color: var(--ink); }

  /* ─── Animations — courtes et fonctionnelles ─────────────────────────── */
  @keyframes fadeIn    { from { opacity:0; } to { opacity:1; } }
  @keyframes fadeInUp  { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
  @keyframes slideDown { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:translateY(0); } }
  @keyframes scaleIn   { from { opacity:0; transform:scale(.96); } to { opacity:1; transform:scale(1); } }
  @keyframes spin      { to { transform: rotate(360deg); } }
  @keyframes pulse     { 0%,100% { opacity:1; } 50% { opacity:.45; } }
  @keyframes shimmer   { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } }
  @keyframes drawCheck { from { stroke-dashoffset: 48; } to { stroke-dashoffset: 0; } }
  @keyframes progressFill { from { width: 0%; } }

  .animate-fadeInUp   { animation: fadeInUp .28s ease both; }
  .animate-fadeInUp-1 { animation: fadeInUp .28s ease .04s both; }
  .animate-fadeInUp-2 { animation: fadeInUp .28s ease .08s both; }
  .animate-fadeInUp-3 { animation: fadeInUp .28s ease .12s both; }
  .animate-fadeInUp-4 { animation: fadeInUp .28s ease .16s both; }
  .animate-scaleIn    { animation: scaleIn .2s ease both; }
  .animate-slideUp    { animation: fadeInUp .28s ease both; }
  .animate-fadeIn     { animation: fadeIn .2s ease both; }

  /* ─── Respect de la préférence « mouvement réduit » ──────────────────── */
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: .01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: .01ms !important;
      scroll-behavior: auto !important;
    }
  }

  /* ─── Boutons ────────────────────────────────────────────────────────── */
  .btn-bounce { transition: background .15s ease, border-color .15s ease, color .15s ease, box-shadow .15s ease; }
  .btn-bounce:active:not(:disabled) { transform: translateY(.5px); }

  /* ─── Cartes ─────────────────────────────────────────────────────────── */
  .card-hover { transition: border-color .15s ease, box-shadow .15s ease; }
  .card-hover:hover {
    border-color: var(--border-strong) !important;
    box-shadow: 0 2px 8px rgba(10,37,64,.07) !important;
  }

  /* ─── Lignes cliquables ──────────────────────────────────────────────── */
  .row-click { transition: background .12s ease; cursor: pointer; }
  .row-click:hover { background: var(--surface-hover); }

  /* ─── Tableaux denses ────────────────────────────────────────────────── */
  .tbl { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 13px; }
  .tbl thead th {
    text-align: left;
    padding: 9px 12px;
    background: var(--surface-alt);
    border-bottom: 1px solid var(--border);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: .04em;
    text-transform: uppercase;
    color: var(--ink-muted);
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 2;
  }
  .tbl tbody td { padding: 10px 12px; border-bottom: 1px solid var(--border); vertical-align: middle; }
  .tbl tbody tr:last-child td { border-bottom: none; }
  .tbl tbody tr { transition: background .12s ease; }
  .tbl tbody tr:hover { background: var(--surface-hover); }
  .tbl .num { text-align: right; font-variant-numeric: tabular-nums; }

  /* Conteneur permettant le défilement horizontal sur mobile */
  .tbl-wrap {
    overflow-x: auto;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    background: var(--surface);
    -webkit-overflow-scrolling: touch;
  }

  /* ─── Squelettes de chargement ───────────────────────────────────────── */
  .skeleton {
    background: linear-gradient(90deg, #eeeeec 25%, #f6f6f4 50%, #eeeeec 75%);
    background-size: 800px 100%;
    animation: shimmer 1.4s linear infinite;
    border-radius: 4px;
  }

  /* ─── Notifications ──────────────────────────────────────────────────── */
  .toast-slide-in  { animation: slideDown .22s cubic-bezier(.16,1,.3,1) both; }
  .toast-slide-out { animation: slideDown .18s ease reverse both; }

  /* ─── Navigation basse (mobile) ──────────────────────────────────────── */
  .bottom-nav {
    position: fixed; bottom: 0; left: 0; right: 0; z-index: 1000;
    background: var(--surface);
    border-top: 1px solid var(--border);
    display: flex; justify-content: space-around; align-items: stretch;
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }
  .bottom-nav-item {
    flex: 1;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    gap: 3px; padding: 8px 4px 7px;
    color: var(--ink-faint);
    font-size: 10.5px; font-weight: 500;
    transition: color .15s ease;
    -webkit-tap-highlight-color: transparent;
    position: relative;
  }
  .bottom-nav-item.active { color: var(--navy); font-weight: 600; }
  .bottom-nav-item.active::before {
    content: ''; position: absolute; top: 0; left: 50%; transform: translateX(-50%);
    width: 24px; height: 2px; background: var(--navy); border-radius: 0 0 2px 2px;
  }

  /* ─── Chronologie de suivi ───────────────────────────────────────────── */
  .timeline-item { display: flex; gap: 14px; position: relative; padding-bottom: 20px; }
  .timeline-item:last-child { padding-bottom: 0; }
  .timeline-line { position: absolute; left: 13px; top: 30px; bottom: 0; width: 1px; background: var(--border); }
  .timeline-item:last-child .timeline-line { display: none; }
  .timeline-dot {
    width: 28px; height: 28px; border-radius: 50%; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    position: relative; z-index: 1;
    border: 1px solid var(--border);
    background: var(--surface);
    color: var(--ink-faint);
  }
  .timeline-dot.completed { background: #f0fdf4; border-color: #bbf7d0; color: var(--success); }
  .timeline-dot.active    { background: var(--navy); border-color: var(--navy); color: #fff; }
  .timeline-dot.pending   { background: var(--surface-alt); color: var(--ink-faint); }

  /* ─── Reçu / ticket ──────────────────────────────────────────────────── */
  .ticket {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    overflow: hidden;
    position: relative;
  }
  .ticket-dashed { border-top: 1px dashed var(--border-strong); margin: 0 16px; }

  /* ─── Champs de formulaire ───────────────────────────────────────────── */
  .field {
    width: 100%;
    padding: 8px 11px;
    border: 1px solid var(--border-strong);
    border-radius: var(--radius);
    background: var(--surface);
    font-size: 14px;
    color: var(--ink);
    transition: border-color .15s ease, box-shadow .15s ease;
    -webkit-appearance: none;
    appearance: none;
  }
  .field::placeholder { color: var(--ink-faint); }
  .field:hover:not(:disabled) { border-color: #b8b8b2; }
  .field:focus {
    outline: none;
    border-color: var(--navy);
    box-shadow: 0 0 0 3px rgba(10,37,64,.10);
  }
  .field:disabled { background: var(--surface-alt); color: var(--ink-faint); cursor: not-allowed; }
  .field[aria-invalid="true"] { border-color: var(--danger); }
  .field[aria-invalid="true"]:focus { box-shadow: 0 0 0 3px rgba(185,28,28,.10); }

  /* Flèche personnalisée pour les listes déroulantes */
  select.field {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 9px center;
    padding-right: 30px;
  }

  /* ─── Onglets ────────────────────────────────────────────────────────── */
  .tabs { display: flex; gap: 2px; border-bottom: 1px solid var(--border); overflow-x: auto; }
  .tab {
    padding: 9px 14px;
    font-size: 13px; font-weight: 500;
    color: var(--ink-muted);
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;
    white-space: nowrap;
    display: flex; align-items: center; gap: 7px;
    transition: color .15s ease, border-color .15s ease;
  }
  .tab:hover { color: var(--ink); }
  .tab.active { color: var(--navy); border-bottom-color: var(--navy); font-weight: 600; }

  /* ─── Administration : navigation latérale ───────────────────────────── */
  .admin-shell { display: flex; align-items: flex-start; }

  .admin-side {
    width: 218px; flex: none;
    background: var(--surface);
    border-right: 1px solid var(--border);
    position: sticky; top: 0;
    height: 100vh; overflow-y: auto;
    padding: 10px 10px 24px;
  }
  .admin-group { display: block; }
  .admin-group + .admin-group { margin-top: 2px; }
  .admin-group-label {
    font-size: 10.5px; font-weight: 600;
    letter-spacing: .11em; text-transform: uppercase;
    color: var(--ink-faint);
    padding: 14px 8px 5px;
  }
  .admin-item {
    display: flex; align-items: center; gap: 9px;
    width: 100%; padding: 7px 9px;
    border-radius: var(--radius);
    font-size: 13.5px; font-weight: 500;
    color: var(--ink-muted); text-align: left;
    transition: background .12s ease, color .12s ease;
  }
  .admin-item:hover { background: var(--surface-hover); color: var(--ink); }
  .admin-item.active {
    background: var(--navy-soft);
    color: var(--navy);
    font-weight: 600;
  }
  .admin-item .admin-count { margin-left: auto; }

  .admin-main { flex: 1; min-width: 0; padding: 18px 20px 40px; }
  .admin-main > .admin-inner { max-width: 1100px; }

  /* Sous 900px : la colonne redevient une barre défilante horizontale.
     Pas de display:contents ici — non supporté par les Android anciens. */
  @media (max-width: 899px) {
    .admin-shell { display: block; }
    .admin-side {
      position: static; width: auto; height: auto;
      border-right: none; border-bottom: 1px solid var(--border);
      display: flex; gap: 4px;
      overflow-x: auto; overflow-y: hidden;
      padding: 8px 12px;
      -webkit-overflow-scrolling: touch;
    }
    .admin-group { display: flex; gap: 4px; }
    .admin-group + .admin-group { margin-top: 0; }
    .admin-group-label { display: none; }
    .admin-item { width: auto; white-space: nowrap; padding: 7px 12px; }
    .admin-main { padding: 14px 12px 32px; }
  }

  /* ─── Utilitaires ────────────────────────────────────────────────────── */
  .mono { font-family: var(--font-mono); font-variant-numeric: tabular-nums; letter-spacing: -.02em; }
  .truncate { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .icon-text { display: inline-flex; align-items: center; gap: 6px; }
  .sr-only {
    position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
    overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0;
  }

  /* ─── Impression ─────────────────────────────────────────────────────── */
  @media print {
    .bottom-nav, .no-print { display: none !important; }
    body { background: #fff; }
    .ticket { border: 1px solid #000; }
  }

  /* ─── Points de rupture ──────────────────────────────────────────────── */
  @media (max-width: 600px) {
    .nav-desktop { display: none !important; }
    .nav-hamburger { display: flex !important; }
    .stepper-label { display: none !important; }
    .stepper-icon { display: flex !important; }
    body { padding-bottom: 60px; }
  }
  @media (min-width: 601px) {
    .nav-hamburger { display: none !important; }
    .nav-mobile-menu { display: none !important; }
    .bottom-nav { display: none !important; }
  }
`;
